import React, { useEffect, useState, Suspense, lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthActions } from './stores/authStore';
import { LoadingScreen } from './components/ui/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Navbar from './components/layout/Navbar';
// AppNavigation removed
import PublicNavbar from './components/PublicNavbar';
import ErrorBoundary from './components/common/ErrorBoundary';
// import { AccessibilityProvider, SkipLinks } from './components/common/AccessibilityProvider';
// import AccessibilityButton from './components/common/AccessibilityButton';
import { usePerformance } from './hooks/usePerformance';
import { performanceMonitor } from './utils/performance';
import { Toaster } from './components/ui/Toaster';
// import './styles/accessibility.css';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/marketing/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const CARegisterPage = lazy(() => import('./pages/CARegisterPage'));
const CALoginPage = lazy(() => import('./pages/CALoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('./pages/vendor/DashboardPage'));
const AnalyticsPage = lazy(() => import('./pages/vendor/AnalyticsPage'));
const SettingsPage = lazy(() => import('./pages/vendor/SettingsPage'));
const GSTPage = lazy(() => import('./pages/vendor/GSTPage'));
const LocationsPage = lazy(() => import('./pages/vendor/LocationsPage'));
const BillingPage = lazy(() => import('./pages/vendor/BillingPage'));
const UsagePage = lazy(() => import('./pages/vendor/UsagePage'));
const NotificationPreferencesPage = lazy(() => import('./components/notifications/NotificationPreferences'));
const NotificationsPage = lazy(() => import('./pages/vendor/NotificationsPage'));
const CreateBusinessProfilePage = lazy(() => import('./pages/vendor/CreateBusinessProfilePage'));
const EditBusinessProfilePage = lazy(() => import('./pages/vendor/EditBusinessProfilePage'));
const BusinessSettingsPage = lazy(() => import('./pages/vendor/BusinessSettingsPage'));
const SubscriptionPage = lazy(() => import('./pages/vendor/SubscriptionPage'));
const FeaturesPage = lazy(() => import('./pages/marketing/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage'));
const SolutionsPage = lazy(() => import('./pages/marketing/SolutionsPage'));
const ResourcesPage = lazy(() => import('./pages/marketing/ResourcesPage'));
const CAPage = lazy(() => import('./pages/marketing/CAPage'));
const InventoryPage = lazy(() => import('./pages/vendor/InventoryPage'));
const OrdersPage = lazy(() => import('./pages/vendor/OrdersPage'));
const CustomersPage = lazy(() => import('./pages/vendor/CustomersPage'));
const SuppliersPage = lazy(() => import('./pages/vendor/SuppliersPage'));
const PurchasesPage = lazy(() => import('./pages/vendor/PurchasesPage'));
const DailySummaryPage = lazy(() => import('./pages/vendor/DailySummaryPage'));
const InvoiceUploadPage = lazy(() => import('./pages/vendor/InvoiceUploadPage'));
const InvoiceDetailPage = lazy(() => import('./pages/vendor/InvoiceDetailPage'));
const InvoiceListPage = lazy(() => import('./pages/vendor/InvoiceListPage'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'));
// CA Pages
import CAPortalLayout from './components/ca/CAPortalLayout';
const CADashboardPage = lazy(() => import('./pages/ca/DashboardPage'));
const ClientMISPage = lazy(() => import('./pages/ca/ClientMISPage'));
const ClientDetailPage = lazy(() => import('./pages/ca/ClientDetailPage'));
const ClientHubPage = lazy(() => import('./pages/ca/ClientHubPage'));
const ComplianceHubPage = lazy(() => import('./pages/ca/ComplianceHubPage'));
const DocumentsPage = lazy(() => import('./pages/ca/DocumentsPage'));
const CASettingsPage = lazy(() => import('./pages/ca/SettingsPage'));
const CASubscriptionPage = lazy(() => import('./pages/ca/SubscriptionPage'));
const CANotificationsPage = lazy(() => import('./pages/ca/NotificationsPage'));
const AddClientPage = lazy(() => import('./pages/ca/AddClientPage'));
const FileReturnPage = lazy(() => import('./pages/ca/FileReturnPage'));
const EditClientDataPage = lazy(() => import('./pages/ca/EditClientDataPage'));
const ReconciliationPage = lazy(() => import('./pages/ca/ReconciliationPage'));
const ReportsPage = lazy(() => import('./pages/ca/ReportsPage'));
const EInvoicePage = lazy(() => import('./pages/ca/EInvoicePage'));
const EWayBillPage = lazy(() => import('./pages/ca/EWayBillPage'));
const GSTR1DocumentViewerPage = lazy(() => import('./pages/ca/GSTR1DocumentViewerPage'));
const GSTR3BEditorPage = lazy(() => import('./pages/ca/GSTR3BEditorPage'));
const LedgerViewerPage = lazy(() => import('./pages/ca/LedgerViewerPage'));
const LedgersPage = lazy(() => import('./pages/ca/LedgersPage'));
const ReturnStatusPage = lazy(() => import('./pages/ca/ReturnStatusPage'));
// Company pages
const AboutPage = lazy(() => import('./pages/marketing/AboutPage'));
const BlogPage = lazy(() => import('./pages/marketing/BlogPage'));
const CareersPage = lazy(() => import('./pages/marketing/CareersPage'));
const ContactPage = lazy(() => import('./pages/marketing/ContactPage'));
// Resources pages
const DocumentationPage = lazy(() => import('./pages/marketing/DocumentationPage'));
const HelpCenterPage = lazy(() => import('./pages/marketing/HelpCenterPage'));
const APIReferencePage = lazy(() => import('./pages/marketing/APIReferencePage'));
const StatusPage = lazy(() => import('./pages/marketing/StatusPage'));
const ArticleDetailPage = lazy(() => import('./pages/marketing/ArticleDetailPage'));
// Legal pages
const PrivacyPage = lazy(() => import('./pages/marketing/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/marketing/TermsPage'));
const SecurityPage = lazy(() => import('./pages/marketing/SecurityPage'));
import Footer from './components/layout/Footer';

import { SidebarProvider, SidebarInset, SidebarTrigger } from './components/ui/sidebar';
import { AppSidebar } from './components/layout/AppSidebar';
import { ChevronRight } from 'lucide-react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"

import ScrollToTop from './components/common/ScrollToTop';
import { initSmoothScroll, destroySmoothScroll } from './lib/smoothScroll';

const PageWrapper = ({ children, pageName }) => (
  <ErrorBoundary name={`${pageName}Page`}>
    <Suspense fallback={<LoadingScreen message={`Loading ${pageName}...`} />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

function AppContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const isCAUser = user?.role === 'ca' || user?.businessType === 'ca_firm';

  // Determine routing context based on user type and path
  const isDashboardRoute = location.pathname.startsWith('/app/') && !isCAUser;
  const isCARoute = (location.pathname.startsWith('/ca/') && !location.pathname.startsWith('/ca/register') && !location.pathname.startsWith('/ca/login')) || (isCAUser && location.pathname === '/');

  // Breadcrumb logic
  const breadcrumbs = useMemo(() => {
    if (!isDashboardRoute) return [];

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const generatedBreadcrumbs = pathSegments
      .filter(segment => segment !== 'app')
      .map((segment, index, filtered) => {
        const originalIndex = pathSegments.indexOf(segment);
        const path = `/${pathSegments.slice(0, originalIndex + 1).join('/')}`;

        let label = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (label.toLowerCase() === 'business profile') {
          label = 'Business Profile';
        } else if (label.toLowerCase() === 'edit') {
          label = 'Edit Business Profile';
        }

        const isLast = index === filtered.length - 1;
        return { path, label, isLast };
      });

    // Special case for Edit Business Profile
    if (location.pathname.includes('/edit-business-profile')) {
      const businessProfileIndex = generatedBreadcrumbs.findIndex(bc => bc.label.toLowerCase() === 'business profile');
      if (businessProfileIndex !== -1) {
        // Remove anything after "Business Profile"
        generatedBreadcrumbs.splice(businessProfileIndex + 1);

        // Add "Edit Business"
        generatedBreadcrumbs.push({
          path: '/app/edit-business-profile',
          label: 'Edit Business',
          isLast: true
        });

        // Mark "Business Profile" as not the last
        generatedBreadcrumbs[businessProfileIndex].isLast = false;
      }
    }
    return generatedBreadcrumbs;
  }, [location.pathname, isDashboardRoute]);

  const { startTiming, endTiming } = usePerformance('AppContent', {
    trackMount: true,
    trackRender: false
  });

  // Track route changes
  useEffect(() => {
    startTiming('route-change');
    const timer = setTimeout(() => {
      endTiming('route-change');
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, startTiming, endTiming]);

  const authPages = ['/login', '/register', '/forgot-password', '/ca/register', '/ca/login', '/onboarding'];
  const isAuthPage = authPages.includes(location.pathname);

  return (
    <div className="min-h-screen bg-slate-50/10 font-sans antialiased">
      {/* Skip Links for Accessibility - Disabled */}
      {/* <SkipLinks /> */}

      {/* Accessibility Button - Disabled */}
      {/* <AccessibilityButton /> */}

      {/* Scroll to Top on Route Change */}
      <ScrollToTop />

      {/* Navigation */}
      {isDashboardRoute ? (
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset className="bg-[#F8F9FA] transition-all flex flex-col">
            <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-4 bg-white px-8 border-b border-slate-200/60 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-16">
              <div className="flex items-center gap-4 flex-1">
                <SidebarTrigger className="h-9 w-9 text-slate-600 hover:text-slate-900 hover:bg-slate-100" />
                <Separator orientation="vertical" className="mr-2 h-4 bg-slate-200" />
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem className="hidden md:block">
                      <BreadcrumbLink href="/app/dashboard" className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors">
                        SOMA
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {breadcrumbs.map((bc, i) => (
                      <React.Fragment key={bc.path}>
                        <BreadcrumbSeparator className="hidden md:block text-slate-300">
                          <ChevronRight size={12} />
                        </BreadcrumbSeparator>
                        <BreadcrumbItem>
                          {bc.isLast ? (
                            <BreadcrumbPage className="text-[11px] font-semibold uppercase tracking-widest text-slate-900">
                              {bc.label}
                            </BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink href={bc.path} className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors">
                              {bc.label}
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                      </React.Fragment>
                    ))}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>

              {/* Global Actions in Header */}
              <div className="flex items-center">
                <Navbar />
              </div>
            </header>

            <main className="flex-1 relative overflow-y-auto focus:outline-none scrollbar-hide bg-white" id="main-content" role="main" tabIndex="-1" style={{ overscrollBehavior: 'contain' }}>
              <div className="p-8">
                <Routes>
                  <Route path="/app/dashboard" element={<ProtectedRoute><PageWrapper pageName="Dashboard"><DashboardPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/inventory" element={<ProtectedRoute><PageWrapper pageName="Inventory"><InventoryPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/orders" element={<ProtectedRoute><PageWrapper pageName="Orders"><OrdersPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/daily-summary" element={<ProtectedRoute><PageWrapper pageName="DailySummary"><DailySummaryPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/daily-summary/:date" element={<ProtectedRoute><PageWrapper pageName="DailySummary"><DailySummaryPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/customers" element={<ProtectedRoute><PageWrapper pageName="Customers"><CustomersPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/suppliers" element={<ProtectedRoute><PageWrapper pageName="Suppliers"><SuppliersPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/purchases" element={<ProtectedRoute><PageWrapper pageName="Purchases"><PurchasesPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/invoices" element={<ProtectedRoute><PageWrapper pageName="Invoices"><InvoiceListPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/invoices/upload" element={<ProtectedRoute><PageWrapper pageName="UploadInvoice"><InvoiceUploadPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/invoices/:invoiceId" element={<ProtectedRoute><PageWrapper pageName="InvoiceDetail"><InvoiceDetailPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/invoices" element={<ProtectedRoute><PageWrapper pageName="Invoices"><InvoiceListPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/analytics" element={<ProtectedRoute><PageWrapper pageName="Analytics"><AnalyticsPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/settings" element={<ProtectedRoute><PageWrapper pageName="Settings"><SettingsPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/notifications" element={<ProtectedRoute><PageWrapper pageName="Notifications"><NotificationsPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/gst" element={<ProtectedRoute><PageWrapper pageName="GST"><GSTPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/locations" element={<ProtectedRoute><PageWrapper pageName="Locations"><LocationsPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/billing" element={<Navigate to="/app/settings?tab=billing" replace />} />
                  <Route path="/app/subscription" element={<ProtectedRoute><PageWrapper pageName="Subscription"><SubscriptionPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/usage" element={<ProtectedRoute><PageWrapper pageName="Usage"><UsagePage /></PageWrapper></ProtectedRoute>} />
                  {/* Profile page removed as per request */}
                  {/* BusinessProfilePage removed as file does not exist, redirecting to BusinessSettings */}
                  <Route path="/app/create-business-profile" element={<ProtectedRoute><PageWrapper pageName="CreateBusinessProfile"><CreateBusinessProfilePage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/business-profile/edit" element={<ProtectedRoute><PageWrapper pageName="EditBusinessProfile"><EditBusinessProfilePage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/business-settings" element={<ProtectedRoute><PageWrapper pageName="BusinessSettings"><BusinessSettingsPage /></PageWrapper></ProtectedRoute>} />
                  <Route path="/app/business-profile" element={<Navigate to="/app/business-settings" replace />} />
                  <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
                </Routes>
              </div>
            </main>
          </SidebarInset>
        </SidebarProvider>
      ) : isCARoute ? (
        <CAPortalLayout>
          <Routes>
            <Route path="/ca/dashboard" element={<ProtectedRoute><PageWrapper pageName="CADashboard"><CADashboardPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients" element={<ProtectedRoute><PageWrapper pageName="ClientHub"><ClientHubPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance" element={<ProtectedRoute><PageWrapper pageName="ComplianceHub"><ComplianceHubPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/documents" element={<ProtectedRoute><PageWrapper pageName="Documents"><DocumentsPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/notifications" element={<ProtectedRoute><PageWrapper pageName="Notifications"><CANotificationsPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/settings" element={<ProtectedRoute><PageWrapper pageName="Settings"><CASettingsPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/subscription" element={<ProtectedRoute><PageWrapper pageName="Subscription"><CASubscriptionPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId" element={<ProtectedRoute><PageWrapper pageName="ClientDetail"><ClientDetailPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/mis" element={<ProtectedRoute><PageWrapper pageName="ClientMIS"><ClientMISPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/edit" element={<ProtectedRoute><PageWrapper pageName="EditClientData"><EditClientDataPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/add" element={<ProtectedRoute><PageWrapper pageName="AddClient"><AddClientPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance/file-return" element={<ProtectedRoute><PageWrapper pageName="FileReturn"><FileReturnPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance/reconciliation" element={<ProtectedRoute><PageWrapper pageName="Reconciliation"><ReconciliationPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance/e-invoice" element={<ProtectedRoute><PageWrapper pageName="EInvoice"><EInvoicePage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance/e-way-bill" element={<ProtectedRoute><PageWrapper pageName="EWayBill"><EWayBillPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/compliance/ledgers" element={<ProtectedRoute><PageWrapper pageName="Ledgers"><LedgersPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/gstr1" element={<ProtectedRoute><PageWrapper pageName="GSTR1Viewer"><GSTR1DocumentViewerPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/gstr3b" element={<ProtectedRoute><PageWrapper pageName="GSTR3BEditor"><GSTR3BEditorPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/ledger" element={<ProtectedRoute><PageWrapper pageName="LedgerViewer"><LedgerViewerPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/clients/:businessId/return-status" element={<ProtectedRoute><PageWrapper pageName="ReturnStatus"><ReturnStatusPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca/reports" element={<ProtectedRoute><PageWrapper pageName="Reports"><ReportsPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/ca" element={<Navigate to="/ca/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/ca/dashboard" replace />} />
          </Routes>
        </CAPortalLayout>
      ) : (
        <>
          {/* Public Navigation - Hidden on Auth Pages */}
          {!isAuthPage && <PublicNavbar id="navigation" />}

          {/* Public Content - Removed pt-16 on Auth Pages */}
          <main id="main-content" role="main" tabIndex="-1" className={!isAuthPage ? "pt-24" : ""}>
            <Routes>
              <Route path="/" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Home"><HomePage /></PageWrapper></ProtectedRoute>} />
              <Route path="/features" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Features"><FeaturesPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/pricing" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Pricing"><PricingPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/solutions" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Solutions"><SolutionsPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/resources" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Resources"><ResourcesPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/ca-services" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="CA Services"><CAPage /></PageWrapper></ProtectedRoute>} />

              {/* Company pages */}
              <Route path="/about" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="About"><AboutPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/blog" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Blog"><BlogPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/blog/:slug" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Blog Post"><ArticleDetailPage type="blog" /></PageWrapper></ProtectedRoute>} />
              <Route path="/careers" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Careers"><CareersPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/contact" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Contact"><ContactPage /></PageWrapper></ProtectedRoute>} />

              {/* Resources pages */}
              <Route path="/docs" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Documentation"><DocumentationPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/docs/:slug" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Documentation Guide"><DocumentationPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Help Center"><HelpCenterPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/help/:slug" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Help Article"><ArticleDetailPage type="help" /></PageWrapper></ProtectedRoute>} />
              <Route path="/api" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="API Reference"><APIReferencePage /></PageWrapper></ProtectedRoute>} />
              <Route path="/status" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Status"><StatusPage /></PageWrapper></ProtectedRoute>} />

              {/* Legal pages */}
              <Route path="/privacy" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Privacy"><PrivacyPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/terms" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Terms"><TermsPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/security" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Security"><SecurityPage /></PageWrapper></ProtectedRoute>} />

              <Route path="/login" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Login"><LoginPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/register" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Register"><RegisterPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/ca/register" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="CARegister"><CARegisterPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/ca/login" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="CALogin"><CALoginPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/forgot-password" element={<ProtectedRoute requireAuth={false}><PageWrapper pageName="Forgot Password"><ForgotPasswordPage /></PageWrapper></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><PageWrapper pageName="Onboarding"><OnboardingPage /></PageWrapper></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* Footer for public pages - Hidden on Auth Pages */}
          {!isAuthPage && <Footer />}
        </>
      )}
      <Toaster />
    </div>
  );
}

function App() {
  const initializeAuth = useAuthActions(state => state.initializeAuth);
  const isInitialized = useAuthActions(state => state.isInitialized);
  const isLoading = useAuthActions(state => state.isLoading);
  const setLoading = useAuthActions(state => state.setLoading);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Initialize authentication state only once
    if (!hasInitialized && !isInitialized) {
      setHasInitialized(true);
      initializeAuth();
    }

    // Initialize performance monitoring
    performanceMonitor.init({
      enableMetrics: true,
      enableUserTiming: true,
      enableResourceTiming: true
    });

    // Log app initialization
    performanceMonitor.mark('app-initialized');

    return () => {
      // Cleanup performance monitoring
      performanceMonitor.cleanup?.();
    };
  }, [initializeAuth, isInitialized, hasInitialized]);

  // Safety fallback: If app is initialized but still loading for too long (stuck state)
  useEffect(() => {
    if (isInitialized && isLoading) {
      const timer = setTimeout(() => {
        console.warn('[App] Force clearing stuck loading state');
        setLoading(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, isLoading, setLoading]);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    initSmoothScroll();
    return () => {
      destroySmoothScroll();
    };
  }, []);

  if (!isInitialized) {
    return <LoadingScreen message="Initializing application..." />;
  }

  return (
    <Router>
      <ErrorBoundary name="App">
        <AppContent />
      </ErrorBoundary>
    </Router>
  );
}

export default App;